import * as React from 'react';
import {
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { CalendarBlank as CalendarBlankIcon } from '@phosphor-icons/react/dist/ssr/CalendarBlank';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import type { Announcement } from '@/types/announcement';
import { Announcement_Status, Announcement_Type } from '@/types/announcement';
import { paths } from '@/paths';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';
import { useCommunityRole } from '@/hooks/v2/use-community-role';
import { RouterLink } from '@/components/core/v2/link';

interface AnnouncementHeaderProps {
  announcement?: Announcement | null;
  isLoading: boolean;
}

const TYPE_COLORS: Record<string, string> = {
  General: '#3B82F6',
  Maintenance: '#F59E0B',
  Emergency: '#EF4444',
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Published: { bg: '#10B98118', text: '#10B981' },
  Pending:   { bg: '#F59E0B18', text: '#F59E0B' },
  Draft:     { bg: '#6B728018', text: '#6B7280' },
  Processing:{ bg: '#6366F118', text: '#6366F1' },
  Cancelled: { bg: '#EF444418', text: '#EF4444' },
  Failed:    { bg: '#EF444418', text: '#EF4444' },
};

export function AnnouncementHeader({ announcement, isLoading }: AnnouncementHeaderProps): React.JSX.Element {
  const navigate = useNavigate();
  const { hasBoardLevelAccess } = useCommunityRole();

  if (isLoading) {
    return (
      <Stack spacing={1.5}>
        <Skeleton height={16} variant="text" width={180} />
        <Skeleton height={28} variant="text" width="50%" />
        <Stack direction="row" spacing={1}>
          <Skeleton height={24} sx={{ borderRadius: 3 }} variant="rectangular" width={80} />
          <Skeleton height={24} sx={{ borderRadius: 3 }} variant="rectangular" width={80} />
          <Skeleton height={14} sx={{ mt: 0.5 }} variant="text" width={120} />
        </Stack>
      </Stack>
    );
  }

  const statusLabel =
    announcement?.status != null
      ? typeof announcement.status === 'number'
        ? (Announcement_Status[announcement.status as keyof typeof Announcement_Status] as string)
        : (announcement.status as string)
      : null;

  const typeLabel =
    announcement?.type != null
      ? typeof announcement.type === 'number'
        ? (Announcement_Type[announcement.type as keyof typeof Announcement_Type] as string)
        : (announcement.type as string)
      : null;

  const statusStyle = statusLabel ? (STATUS_COLORS[statusLabel] ?? { bg: '#6B728018', text: '#6B7280' }) : null;
  const typeColor = typeLabel ? (TYPE_COLORS[typeLabel] ?? '#6B7280') : '#6B7280';

  return (
    <Stack spacing={1.5}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator="›" sx={{ fontSize: '0.8125rem' }}>
        <Link
          color="text.secondary"
          component={RouterLink}
          href={paths.announcements.index}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { color: 'text.primary' } }}
          underline="hover"
        >
          <ArrowLeftIcon size={14} />
          Announcements
        </Link>
        <Typography color="text.primary" sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>
          {announcement?.subject || 'Announcement'}
        </Typography>
      </Breadcrumbs>

      {/* Title */}
      <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3 }}>
        {announcement?.subject || 'Untitled Announcement'}
      </Typography>

      {/* Meta row */}
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        {typeLabel && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1,
              py: '2px',
              borderRadius: 1,
              bgcolor: `${typeColor}18`,
              border: `1px solid ${typeColor}40`,
            }}
          >
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: typeColor, lineHeight: 1.4 }}>
              {toTitleCase(typeLabel)}
            </Typography>
          </Box>
        )}

        {hasBoardLevelAccess && statusStyle && statusLabel && (() => {
          const scheduledInFuture = !!announcement?.scheduled_at && new Date(announcement.scheduled_at) > new Date();
          const isSending = ['pending', 'processing'].includes(statusLabel.toLowerCase()) && !scheduledInFuture;
          const displayLabel = isSending ? 'Sending…' : toTitleCase(statusLabel);
          return (
            <Tooltip
              title={isSending ? 'Your announcement has been queued and is being delivered to members. This page updates automatically.' : ''}
              placement="bottom"
            >
              <Chip
                icon={isSending ? <CircularProgress size={10} sx={{ color: `${statusStyle.text} !important`, ml: '6px !important' }} /> : undefined}
                label={displayLabel}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  bgcolor: statusStyle.bg,
                  color: statusStyle.text,
                  border: `1px solid ${statusStyle.text}40`,
                  '& .MuiChip-label': { pl: isSending ? 0.5 : undefined },
                }}
              />
            </Tooltip>
          );
        })()}

        {hasBoardLevelAccess && announcement?.board_members_only && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1,
              py: '2px',
              borderRadius: 1,
              bgcolor: 'primary.main',
            }}
          >
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#fff', lineHeight: 1.4 }}>
              Board Only
            </Typography>
          </Box>
        )}

        {/* Managers see the drafted date; residents see when it actually went out. */}
        {(() => {
          const when = hasBoardLevelAccess
            ? announcement?.created_at
            : (announcement?.processed_on ?? announcement?.created_at);
          if (!when) return null;
          return (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <CalendarBlankIcon color="var(--mui-palette-text-disabled)" size={14} />
              <Typography color="text.disabled" sx={{ fontSize: '0.8125rem' }}>
                {dayjs(when).format('MMM DD, YYYY')}
              </Typography>
            </Stack>
          );
        })()}
      </Stack>
    </Stack>
  );
}
